import {
  useRef,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type WheelEvent as ReactWheelEvent,
} from "react";
import { X } from "lucide-react";
import { Label } from "@/components/ui/label";

export type DurationSliderVariant = "horizontal" | "wheel";

export const USE_DURATION_WHEEL = true;

export type DurationSliderProps = {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  min?: number;
  max?: number;
  minuteIncrement?: 1 | 5;
  variant?: DurationSliderVariant;
};

export function getDurationSliderMax(value: string, minimumMax = 240) {
  const parsedDuration = Number.parseInt(value, 10);
  return Number.isFinite(parsedDuration)
    ? Math.max(minimumMax, parsedDuration + 60)
    : minimumMax;
}

export function getDurationWheelLabelValues(value: number) {
  const revolutionStart = Math.floor(value / 60) * 60;
  return [revolutionStart, revolutionStart + 15, revolutionStart + 30, revolutionStart + 45];
}

export default function DurationSlider({
  value,
  onChange,
  onBlur,
  min = 0,
  max = getDurationSliderMax(value),
  minuteIncrement = 1,
  variant = USE_DURATION_WHEEL ? "wheel" : "horizontal",
}: DurationSliderProps) {
  const sharedProps = { value, onChange, onBlur, min, max, step: minuteIncrement };

  return variant === "wheel"
    ? <DurationWheel {...sharedProps} />
    : <HorizontalDurationSlider {...sharedProps} />;
}

type DurationRendererProps = Omit<DurationSliderProps, "variant"> & {
  min: number;
  max: number;
  step: number;
};

function HorizontalDurationSlider({
  value,
  onChange,
  onBlur,
  min,
  max,
  step,
}: DurationRendererProps) {
  return (
    <div className="duration-slider horizontal">
      <div className="duration-slider-header">
        <Label className="duration-slider-label">Duration</Label>
        <div className="duration-slider-header-actions">
          <DurationValueInput value={value} onChange={onChange} onBlur={onBlur} min={min} max={max} step={step} />
          <DurationClearButton value={value} onClear={() => onChange("")} />
        </div>
      </div>
      <input
        className="duration-slider-range"
        type="range"
        min={min}
        max={max}
        step={step}
        value={value || min}
        aria-label="Duration"
        onChange={event => onChange(event.target.value)}
        onBlur={onBlur}
      />
    </div>
  );
}

function DurationWheel({
  value,
  onChange,
  onBlur,
  min,
  max,
  step,
}: DurationRendererProps) {
  const drag = useRef<{ previousAngle: number; degrees: number } | null>(null);
  const numericValue = parseDuration(value, min);
  const rotation = numericValue * 6;
  const [top, right, bottom, left] = getDurationWheelLabelValues(numericValue);
  const wheelLabels = [
    { position: "top", value: top },
    { position: "right", value: right },
    { position: "bottom", value: bottom },
    { position: "left", value: left },
  ];

  function updateFromPointer(event: ReactPointerEvent<HTMLDivElement>) {
    if (!drag.current) return;
    const angle = pointerAngle(event);
    const delta = normalizeAngleDelta(angle - drag.current.previousAngle);
    drag.current.previousAngle = angle;
    drag.current.degrees += delta;
    const nextMinutes = clamp(roundToStep(drag.current.degrees / 6, step), min, max);
    onChange(String(nextMinutes));
  }

  function stopDragging(event: ReactPointerEvent<HTMLDivElement>) {
    if (!drag.current) return;
    drag.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    onBlur?.();
  }

  function updateFromWheel(event: ReactWheelEvent<HTMLDivElement>) {
    event.preventDefault();
    event.currentTarget.focus();
    const delta = Math.abs(event.deltaY) >= Math.abs(event.deltaX) ? event.deltaY : event.deltaX;
    if (delta === 0) return;
    const direction = delta > 0 ? 1 : -1;
    const nextMinutes = clamp(roundToStep(numericValue + direction * step, step), min, max);
    onChange(String(nextMinutes));
  }

  return (
    <div className="duration-slider wheel">
      <div className="duration-slider-header">
        <Label className="duration-slider-label">Duration</Label>
        <DurationClearButton value={value} onClear={() => onChange("")} />
      </div>
      <div className="duration-wheel-control">
        <div
          className="duration-wheel-surface"
          role="slider"
          tabIndex={0}
          aria-label="Duration"
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={numericValue}
          aria-valuetext={`${numericValue} minutes`}
          style={{ "--duration-wheel-angle": `${rotation}deg` } as CSSProperties}
          onPointerDown={event => {
            event.preventDefault();
            event.currentTarget.setPointerCapture(event.pointerId);
            drag.current = { previousAngle: pointerAngle(event), degrees: rotation };
          }}
          onPointerMove={updateFromPointer}
          onPointerUp={stopDragging}
          onPointerCancel={stopDragging}
          onWheel={updateFromWheel}
          onBlur={onBlur}
          onKeyDown={event => {
            let nextValue: number | null = null;
            if (event.key === "ArrowUp" || event.key === "ArrowRight") nextValue = numericValue + step;
            if (event.key === "ArrowDown" || event.key === "ArrowLeft") nextValue = numericValue - step;
            if (event.key === "PageUp") nextValue = numericValue + 60;
            if (event.key === "PageDown") nextValue = numericValue - 60;
            if (event.key === "Home") nextValue = min;
            if (event.key === "End") nextValue = max;
            if (nextValue == null) return;
            event.preventDefault();
            onChange(String(clamp(roundToStep(nextValue, step), min, max)));
          }}
        >
          <div className="duration-wheel-dial" aria-hidden="true">
            <div className="duration-wheel-ticks">
              {Array.from({ length: 12 }, (_, index) => (
                <span
                  key={index}
                  className={`duration-wheel-tick${index % 3 === 0 ? " quarter-hour" : ""}`}
                  style={{ "--duration-wheel-tick-angle": `${index * 30}deg` } as CSSProperties}
                >
                  <span />
                </span>
              ))}
            </div>
            {wheelLabels.map(label => (
              <span key={label.position} className={`duration-wheel-number ${label.position}`}>
                {label.value}
              </span>
            ))}
            <span className="duration-wheel-marker-track">
              <span className="duration-wheel-marker" />
            </span>
          </div>
        </div>
        <DurationValueInput value={value} onChange={onChange} onBlur={onBlur} min={min} max={max} step={step} />
      </div>
    </div>
  );
}

function DurationClearButton({ value, onClear }: { value: string; onClear: () => void }) {
  if (!value) return null;

  return (
    <button
      type="button"
      className="duration-slider-clear"
      aria-label="Clear duration"
      title="Clear duration"
      onClick={onClear}
    >
      <X />
    </button>
  );
}

function DurationValueInput({
  value,
  onChange,
  onBlur,
  min,
  max,
  step,
}: Pick<DurationRendererProps, "value" | "onChange" | "onBlur" | "min" | "max" | "step">) {
  function commitValue() {
    if (value) {
      const numericValue = Number(value);
      if (Number.isFinite(numericValue)) {
        onChange(String(clamp(roundToStep(numericValue, step), min, max)));
      }
    }
    onBlur?.();
  }

  return (
    <input
      className="duration-slider-value"
      type="number"
      min={min}
      step={step}
      inputMode="numeric"
      value={value}
      placeholder="--"
      aria-label="Duration in minutes"
      onChange={event => onChange(event.target.value)}
      onFocus={event => event.currentTarget.select()}
      onBlur={commitValue}
    />
  );
}

function pointerAngle(event: ReactPointerEvent<HTMLElement>) {
  const bounds = event.currentTarget.getBoundingClientRect();
  const x = event.clientX - (bounds.left + bounds.width / 2);
  const y = event.clientY - (bounds.top + bounds.height / 2);
  return Math.atan2(y, x) * 180 / Math.PI;
}

export function normalizeAngleDelta(delta: number) {
  if (delta > 180) return delta - 360;
  if (delta < -180) return delta + 360;
  return delta;
}

function parseDuration(value: string, fallback: number) {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function roundToStep(value: number, step: number) {
  return Math.round(value / step) * step;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}
